import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  createAppLandingRule,
  createAppLandingScreen,
  deleteAppLandingOverride,
  deleteAppLandingRule,
  getAppLandingAudit,
  getAppLandingDistribution,
  getAppLandingPayload,
  getAppLandingStats,
  isAppLandingApiSuccess,
  listAppLandingOverrides,
  listAppLandingRules,
  listAppLandingScreens,
  patchAppLandingRule,
  patchAppLandingScreen,
  previewAppLanding,
  setAppLandingRuleEnabled,
  upsertAppLandingOverride,
} from '../../services/appLandingApi';
import {
  AUDIENCE_OPTIONS,
  PLATFORM_OPTIONS,
  SUBJECT_TYPE_OPTIONS,
  audienceLabel,
  buildRulePayload,
  buildScreenPayload,
  computeBucketRanges,
  emptyOverrideForm,
  emptyRuleForm,
  emptyScreenForm,
  formatBucketRange,
  formatDateTime,
  formatNumber,
  formatPercent,
  formatSchedule,
  formatVersionWindow,
  isSessionExpired,
  normalizeAudit,
  normalizeOverrides,
  normalizeRules,
  normalizeScreens,
  randomSalt,
  ruleToForm,
  screenLabel,
  screenToForm,
  validateRuleForm,
  validateScreenForm,
  variantsForEditor,
} from './appLandingUtils';
import { WeightBar, WeightEditor } from './WeightSplit';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

const TABS = [
  { id: 'rules', label: 'Rules' },
  { id: 'stats', label: 'Stats' },
  { id: 'preview', label: 'Preview' },
  { id: 'overrides', label: 'Overrides' },
  { id: 'screens', label: 'Screens' },
  { id: 'audit', label: 'Audit' },
];

function AppLanding() {
  const [tab, setTab] = useState('rules');
  const [helpOpen, setHelpOpen] = useState(false);

  const [rules, setRules] = useState([]);
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSessionExpired = useCallback(
    (res) => {
      if (!isSessionExpired(res)) return false;
      showSnackbar('Session expired. Please login again.', 'error');
      setTimeout(() => {
        window.location = '/login';
      }, 1500);
      return true;
    },
    [showSnackbar]
  );

  const fetchRules = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await listAppLandingRules();
      if (handleSessionExpired(res)) return;
      if (!isAppLandingApiSuccess(res)) {
        setError(res?.message || 'Failed to load landing rules.');
        setRules([]);
        return;
      }
      const payload = getAppLandingPayload(res) || {};
      setRules(normalizeRules(payload));
      const fromRules = normalizeScreens(payload);
      if (fromRules.length) setScreens(fromRules);
    } catch (err) {
      setError(err?.message || 'Failed to load landing rules.');
      setRules([]);
    } finally {
      setLoading(false);
    }
  }, [handleSessionExpired]);

  const fetchScreens = useCallback(
    async ({ silent } = {}) => {
      try {
        const res = await listAppLandingScreens();
        if (handleSessionExpired(res)) return;
        if (!isAppLandingApiSuccess(res)) {
          if (!silent) showSnackbar(res?.message || 'Failed to load screens.', 'error');
          return;
        }
        setScreens(normalizeScreens(getAppLandingPayload(res)));
      } catch {
        if (!silent) showSnackbar('Failed to load screens.', 'error');
      }
    },
    [handleSessionExpired, showSnackbar]
  );

  useEffect(() => {
    fetchRules();
    fetchScreens({ silent: true });
  }, [fetchRules, fetchScreens]);

  // --- Rule editor ---
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState(emptyRuleForm());
  const [ruleErrors, setRuleErrors] = useState({});
  const [ruleFormError, setRuleFormError] = useState('');
  const [savingRule, setSavingRule] = useState(false);
  const [togglingRuleId, setTogglingRuleId] = useState(null);
  const [deleteRule, setDeleteRule] = useState(null);
  const [deletingRule, setDeletingRule] = useState(false);
  const [reshuffleRule, setReshuffleRule] = useState(null);
  const [reshuffling, setReshuffling] = useState(false);

  const setRuleField = (key, value) => {
    setRuleForm((prev) => ({ ...prev, [key]: value }));
  };

  const openCreateRule = () => {
    const form = emptyRuleForm();
    form.variants = variantsForEditor(form.variants, screens);
    if (screens.some((s) => s.screen_key === 'home')) form.fallback_screen_key = 'home';
    else if (screens[0]) form.fallback_screen_key = screens[0].screen_key;
    setEditingRule(null);
    setRuleForm(form);
    setRuleErrors({});
    setRuleFormError('');
    setDrawerOpen(true);
  };

  const openEditRule = (rule) => {
    setEditingRule(rule);
    setRuleForm(ruleToForm(rule, screens));
    setRuleErrors({});
    setRuleFormError('');
    setDrawerOpen(true);
  };

  const handleSaveRule = async () => {
    const errors = validateRuleForm(ruleForm);
    setRuleErrors(errors);
    if (Object.keys(errors).length) {
      setRuleFormError(errors.variants || 'Fix the highlighted fields.');
      return;
    }
    setSavingRule(true);
    setRuleFormError('');
    try {
      const body = buildRulePayload(ruleForm);
      const res = editingRule
        ? await patchAppLandingRule(editingRule.id, body)
        : await createAppLandingRule(body);
      if (handleSessionExpired(res)) return;
      if (!isAppLandingApiSuccess(res)) {
        setRuleFormError(res?.message || 'Save failed.');
        return;
      }
      showSnackbar(editingRule ? 'Rule updated. Cache invalidates immediately.' : 'Rule created.');
      setDrawerOpen(false);
      fetchRules();
    } catch {
      setRuleFormError('Save failed.');
    } finally {
      setSavingRule(false);
    }
  };

  const handleToggleRule = async (rule) => {
    setTogglingRuleId(rule.id);
    try {
      const res = await setAppLandingRuleEnabled(rule.id, !rule.is_enabled);
      if (handleSessionExpired(res)) return;
      if (!isAppLandingApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to update rule.', 'error');
        return;
      }
      setRules((prev) =>
        prev.map((r) => (r.id === rule.id ? { ...r, is_enabled: !rule.is_enabled } : r))
      );
      showSnackbar(
        !rule.is_enabled
          ? `${rule.name} enabled.`
          : `${rule.name} disabled. Subjects fall through to the next matching rule.`
      );
    } catch {
      showSnackbar('Failed to update rule.', 'error');
    } finally {
      setTogglingRuleId(null);
    }
  };

  const handleDeleteRule = async () => {
    if (!deleteRule) return;
    setDeletingRule(true);
    try {
      const res = await deleteAppLandingRule(deleteRule.id);
      if (handleSessionExpired(res)) return;
      if (!isAppLandingApiSuccess(res)) {
        showSnackbar(res?.message || 'Cannot delete this rule. Disable it instead.', 'error');
        return;
      }
      showSnackbar('Rule deleted.');
      setDeleteRule(null);
      fetchRules();
    } catch {
      showSnackbar('Delete failed.', 'error');
    } finally {
      setDeletingRule(false);
    }
  };

  const handleReshuffle = async () => {
    if (!reshuffleRule) return;
    setReshuffling(true);
    try {
      const salt = randomSalt();
      const res = await patchAppLandingRule(reshuffleRule.id, { salt });
      if (handleSessionExpired(res)) return;
      if (!isAppLandingApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to change salt.', 'error');
        return;
      }
      showSnackbar(`Salt changed to ${salt}. Assignments re-roll on the next config read.`);
      setReshuffleRule(null);
      fetchRules();
    } catch {
      showSnackbar('Failed to change salt.', 'error');
    } finally {
      setReshuffling(false);
    }
  };

  // --- Screens ---
  const [screenDialogOpen, setScreenDialogOpen] = useState(false);
  const [editingScreen, setEditingScreen] = useState(null);
  const [screenForm, setScreenForm] = useState(emptyScreenForm());
  const [screenErrors, setScreenErrors] = useState({});
  const [screenFormError, setScreenFormError] = useState('');
  const [savingScreen, setSavingScreen] = useState(false);
  const [togglingScreenKey, setTogglingScreenKey] = useState(null);

  const openCreateScreen = () => {
    setEditingScreen(null);
    setScreenForm(emptyScreenForm());
    setScreenErrors({});
    setScreenFormError('');
    setScreenDialogOpen(true);
  };

  const openEditScreen = (row) => {
    setEditingScreen(row);
    setScreenForm(screenToForm(row));
    setScreenErrors({});
    setScreenFormError('');
    setScreenDialogOpen(true);
  };

  const handleSaveScreen = async () => {
    const isCreate = !editingScreen;
    const errors = validateScreenForm(screenForm, { isCreate });
    setScreenErrors(errors);
    if (Object.keys(errors).length) {
      setScreenFormError('Fix the highlighted fields.');
      return;
    }
    setSavingScreen(true);
    setScreenFormError('');
    try {
      const body = buildScreenPayload(screenForm, { isCreate });
      const res = isCreate
        ? await createAppLandingScreen(body)
        : await patchAppLandingScreen(editingScreen.screen_key, body);
      if (handleSessionExpired(res)) return;
      if (!isAppLandingApiSuccess(res)) {
        setScreenFormError(res?.message || 'Save failed.');
        return;
      }
      showSnackbar(isCreate ? 'Screen created.' : 'Screen updated.');
      setScreenDialogOpen(false);
      fetchScreens();
      fetchRules();
    } catch {
      setScreenFormError('Save failed.');
    } finally {
      setSavingScreen(false);
    }
  };

  const handleToggleScreen = async (row) => {
    setTogglingScreenKey(row.screen_key);
    try {
      const res = await patchAppLandingScreen(row.screen_key, { is_enabled: !row.is_enabled });
      if (handleSessionExpired(res)) return;
      if (!isAppLandingApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to update screen.', 'error');
        return;
      }
      setScreens((prev) =>
        prev.map((s) =>
          s.screen_key === row.screen_key ? { ...s, is_enabled: !row.is_enabled } : s
        )
      );
      showSnackbar(`${row.label || row.screen_key} ${!row.is_enabled ? 'enabled' : 'disabled'}.`);
    } catch {
      showSnackbar('Failed to update screen.', 'error');
    } finally {
      setTogglingScreenKey(null);
    }
  };

  // --- Overrides ---
  const [overrides, setOverrides] = useState([]);
  const [overridesLoading, setOverridesLoading] = useState(false);
  const [overrideForm, setOverrideForm] = useState(emptyOverrideForm());
  const [savingOverride, setSavingOverride] = useState(false);
  const [deletingOverrideId, setDeletingOverrideId] = useState(null);

  const fetchOverrides = useCallback(async () => {
    setOverridesLoading(true);
    try {
      const res = await listAppLandingOverrides();
      if (handleSessionExpired(res)) return;
      if (!isAppLandingApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to load overrides.', 'error');
        return;
      }
      setOverrides(normalizeOverrides(getAppLandingPayload(res)));
    } catch {
      showSnackbar('Failed to load overrides.', 'error');
    } finally {
      setOverridesLoading(false);
    }
  }, [handleSessionExpired, showSnackbar]);

  useEffect(() => {
    if (tab === 'overrides') fetchOverrides();
  }, [tab, fetchOverrides]);

  const handleSaveOverride = async () => {
    if (!overrideForm.subject_key.trim()) {
      showSnackbar('Subject key is required.', 'error');
      return;
    }
    setSavingOverride(true);
    try {
      const res = await upsertAppLandingOverride({
        subject_type: overrideForm.subject_type,
        subject_key: overrideForm.subject_key.trim(),
        screen_key: overrideForm.screen_key,
        note: overrideForm.note.trim() || null,
      });
      if (handleSessionExpired(res)) return;
      if (!isAppLandingApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to save override.', 'error');
        return;
      }
      showSnackbar('Override saved. This device bypasses every rule.');
      setOverrideForm(emptyOverrideForm());
      fetchOverrides();
    } catch {
      showSnackbar('Failed to save override.', 'error');
    } finally {
      setSavingOverride(false);
    }
  };

  const handleDeleteOverride = async (row) => {
    setDeletingOverrideId(row.id);
    try {
      const res = await deleteAppLandingOverride(row.id);
      if (handleSessionExpired(res)) return;
      if (!isAppLandingApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to delete override.', 'error');
        return;
      }
      showSnackbar('Override removed.');
      fetchOverrides();
    } catch {
      showSnackbar('Failed to delete override.', 'error');
    } finally {
      setDeletingOverrideId(null);
    }
  };

  // --- Preview ---
  const [previewForm, setPreviewForm] = useState({
    installation_id: '',
    user_id: '',
    app_version: '',
    platform: 'android',
  });
  const [previewResult, setPreviewResult] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const handlePreview = async () => {
    if (!previewForm.installation_id.trim() && !previewForm.user_id.trim()) {
      showSnackbar('Pass an installation_id and/or user_id.', 'error');
      return;
    }
    setPreviewing(true);
    setPreviewResult(null);
    try {
      const body = {
        platform: previewForm.platform || undefined,
      };
      if (previewForm.installation_id.trim()) body.installation_id = previewForm.installation_id.trim();
      if (previewForm.user_id.trim()) body.user_id = previewForm.user_id.trim();
      if (previewForm.app_version.trim()) body.app_version = previewForm.app_version.trim();
      const res = await previewAppLanding(body);
      if (handleSessionExpired(res)) return;
      if (!isAppLandingApiSuccess(res)) {
        showSnackbar(res?.message || 'Preview failed.', 'error');
        return;
      }
      setPreviewResult(getAppLandingPayload(res) || res);
    } catch {
      showSnackbar('Preview failed.', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  // --- Stats ---
  const [statsDays, setStatsDays] = useState(7);
  const [distVersion, setDistVersion] = useState('');
  const [stats, setStats] = useState(null);
  const [distribution, setDistribution] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [statsRes, distRes] = await Promise.all([
        getAppLandingStats({ days: statsDays }),
        getAppLandingDistribution({ appVersion: distVersion || undefined, days: statsDays }),
      ]);
      if (handleSessionExpired(statsRes) || handleSessionExpired(distRes)) return;
      if (!isAppLandingApiSuccess(statsRes)) {
        showSnackbar(statsRes?.message || 'Failed to load stats.', 'error');
      } else {
        setStats(getAppLandingPayload(statsRes));
      }
      if (!isAppLandingApiSuccess(distRes)) {
        showSnackbar(distRes?.message || 'Failed to load distribution.', 'error');
      } else {
        setDistribution(getAppLandingPayload(distRes));
      }
    } catch {
      showSnackbar('Failed to load stats.', 'error');
    } finally {
      setStatsLoading(false);
    }
  }, [statsDays, distVersion, handleSessionExpired, showSnackbar]);

  useEffect(() => {
    if (tab === 'stats') fetchStats();
  }, [tab, fetchStats]);

  const statsRows = useMemo(() => {
    const payload = stats || {};
    const rows =
      payload.screens || payload.items || payload.rows || payload.by_screen || payload.byScreen || [];
    return Array.isArray(rows) ? rows : [];
  }, [stats]);

  const distRows = useMemo(() => {
    const payload = distribution || {};
    const configured =
      payload.configured || payload.weights || payload.screens || payload.variants || [];
    return Array.isArray(configured) ? configured : [];
  }, [distribution]);

  const observedByKey = useMemo(() => {
    const payload = distribution || stats || {};
    const observed = payload.observed || payload.actual || [];
    const map = {};
    (Array.isArray(observed) ? observed : []).forEach((row) => {
      const key = row.screen_key || row.screenKey;
      if (key) map[key] = row;
    });
    statsRows.forEach((row) => {
      const key = row.screen_key || row.screenKey;
      if (key) map[key] = { ...map[key], ...row };
    });
    return map;
  }, [distribution, stats, statsRows]);

  const chartData = useMemo(() => {
    const keys = new Set();
    distRows.forEach((r) => keys.add(r.screen_key || r.screenKey));
    statsRows.forEach((r) => keys.add(r.screen_key || r.screenKey));
    if (keys.size === 0) screens.forEach((s) => keys.add(s.screen_key));
    return Array.from(keys)
      .filter(Boolean)
      .map((key) => {
        const configured = distRows.find((r) => (r.screen_key || r.screenKey) === key);
        const observed = observedByKey[key] || {};
        const configuredWeight = Number(configured?.weight ?? configured?.configured_weight ?? 0);
        const observedShare = observed.share ?? observed.observed_share ?? observed.pct;
        return {
          name: screenLabel(screens, key),
          configured: configuredWeight,
          observed: observedShare != null ? (Number(observedShare) <= 1 ? Number(observedShare) * 100 : Number(observedShare)) : 0,
          return_rate:
            observed.return_rate != null
              ? Number(observed.return_rate) <= 1
                ? Number(observed.return_rate) * 100
                : Number(observed.return_rate)
              : 0,
        };
      });
  }, [distRows, statsRows, observedByKey, screens]);

  // --- Audit ---
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [expandedAudit, setExpandedAudit] = useState(null);

  const fetchAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await getAppLandingAudit({ limit: 50 });
      if (handleSessionExpired(res)) return;
      if (!isAppLandingApiSuccess(res)) {
        showSnackbar(res?.message || 'Failed to load audit log.', 'error');
        return;
      }
      setAuditRows(normalizeAudit(getAppLandingPayload(res)));
    } catch {
      showSnackbar('Failed to load audit log.', 'error');
    } finally {
      setAuditLoading(false);
    }
  }, [handleSessionExpired, showSnackbar]);

  useEffect(() => {
    if (tab === 'audit') fetchAudit();
  }, [tab, fetchAudit]);

  const sortedRules = useMemo(
    () => [...rules].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0)),
    [rules]
  );

  const previewScreenKey =
    previewResult?.screen_key || previewResult?.screenKey || previewResult?.landing_screen;

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 1 }}>
            <IconButton style={{ background: '#FFFFFF' }} component={Link} to="/">
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5">App landing screen</Typography>
              <Typography variant="body2" color="text.secondary">
                Controls which tab the app opens on at cold start. Seeded 100% Home until you change a rule.
              </Typography>
            </Box>
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => {
                  fetchRules();
                  if (tab === 'stats') fetchStats();
                  if (tab === 'overrides') fetchOverrides();
                  if (tab === 'audit') fetchAudit();
                  if (tab === 'screens') fetchScreens();
                }}
                disabled={loading}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {tab === 'rules' && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateRule}>
                New rule
              </Button>
            )}
            {tab === 'screens' && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateScreen}>
                Add screen
              </Button>
            )}
          </Box>

          <Button
            size="small"
            startIcon={<InfoOutlinedIcon />}
            onClick={() => setHelpOpen((v) => !v)}
            sx={{ mb: 1 }}
          >
            {helpOpen ? 'Hide how assignment works' : 'How assignment works'}
          </Button>

          <Collapse in={helpOpen}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <code>bucket = sha256(rule.salt + &quot;:&quot; + subject_key) % 10000</code>. The bucket is
                permanent for an install. Editing one weight only moves subjects whose bucket crosses the
                shifted boundary; raising a weight never moves anyone <em>out</em> of that screen. Change the
                salt to re-randomise everybody.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Rules run by priority ascending, first match wins. Keep a catch-all (seeded Default is
                priority 1000). A landing screen is never sent to a build that cannot render it — the rule
                fallback is used instead, and weights are not redistributed.
              </Typography>
              <Typography variant="body2">
                Kill switch: disable the experiment rule. Everyone falls through to the Home catch-all on
                the next config read (Redis 60s, in-memory 10s).
              </Typography>
            </Alert>
          </Collapse>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            {TABS.map((item) => (
              <Tab key={item.id} value={item.id} label={item.label} />
            ))}
          </Tabs>

          {tab === 'rules' && (
            <Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : sortedRules.length === 0 ? (
                <Typography color="text.secondary">No rules found.</Typography>
              ) : (
                sortedRules.map((rule) => {
                  const ranges = computeBucketRanges(rule.variants);
                  return (
                    <Paper key={rule.id} sx={{ p: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="h6">{rule.name}</Typography>
                            <Chip size="small" label={`priority ${rule.priority}`} />
                            <Chip size="small" label={audienceLabel(rule.audience)} variant="outlined" />
                            {rule.platform ? (
                              <Chip size="small" label={rule.platform} variant="outlined" />
                            ) : null}
                            {rule.exclude_test_users ? (
                              <Chip size="small" label="excludes QA" variant="outlined" />
                            ) : null}
                          </Box>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                            {formatVersionWindow(rule.min_app_version, rule.max_app_version)} ·{' '}
                            {formatSchedule(rule.starts_at, rule.ends_at)} · salt {rule.salt} · fallback{' '}
                            {screenLabel(screens, rule.fallback_screen_key)}
                          </Typography>
                          <WeightBar variants={rule.variants} screens={screens} />
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            {ranges.map((v) => (
                              <Chip
                                key={v.screen_key}
                                size="small"
                                label={`${screenLabel(screens, v.screen_key)} ${v.weight}%  ${formatBucketRange(
                                  v.range_start,
                                  v.range_end
                                )}`}
                              />
                            ))}
                          </Box>
                          {rule.notes ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {rule.notes}
                            </Typography>
                          ) : null}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={!!rule.is_enabled}
                                disabled={togglingRuleId === rule.id}
                                onChange={() => handleToggleRule(rule)}
                              />
                            }
                            label={rule.is_enabled ? 'On' : 'Off'}
                          />
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEditRule(rule)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Change salt (re-randomise)">
                            <IconButton size="small" onClick={() => setReshuffleRule(rule)}>
                              <ShuffleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => setDeleteRule(rule)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })
              )}
            </Box>
          )}

          {tab === 'stats' && (
            <Box>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                  <TextField
                    select
                    size="small"
                    label="Days"
                    value={statsDays}
                    onChange={(e) => setStatsDays(Number(e.target.value))}
                    sx={{ minWidth: 120 }}
                  >
                    <MenuItem value={7}>Last 7 days</MenuItem>
                    <MenuItem value={14}>Last 14 days</MenuItem>
                    <MenuItem value={30}>Last 30 days</MenuItem>
                  </TextField>
                  <TextField
                    size="small"
                    label="App version (distribution)"
                    placeholder="2.4.0"
                    value={distVersion}
                    onChange={(e) => setDistVersion(e.target.value)}
                  />
                  <Button variant="contained" onClick={fetchStats} disabled={statsLoading}>
                    {statsLoading ? 'Loading…' : 'Refresh'}
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Observed opens come from <code>user_events.metadata.landing_screen</code> on APP_VISIT.
                  Return rate is the share of users who came back on a later day — watch that when picking a
                  winner. Stats are cached 5 minutes.
                </Typography>
              </Paper>

              {statsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {chartData.length > 0 && (
                    <Paper sx={{ p: 2, mb: 2, height: 320 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        Configured vs observed
                      </Typography>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <RechartsTooltip />
                          <Legend />
                          <Bar dataKey="configured" name="Configured %" fill="#1976d2" />
                          <Bar dataKey="observed" name="Observed %" fill="#ed6c02" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  )}

                  <TableContainer component={Paper} sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Screen</TableCell>
                          <TableCell>Configured</TableCell>
                          <TableCell>Buckets</TableCell>
                          <TableCell>Servable</TableCell>
                          <TableCell align="right">Opens</TableCell>
                          <TableCell align="right">Users</TableCell>
                          <TableCell align="right">Return rate</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(distRows.length ? distRows : statsRows).map((row, index) => {
                          const key = row.screen_key || row.screenKey;
                          const observed = observedByKey[key] || row;
                          const start = row.range_start ?? row.rangeStart ?? row.bucket_start;
                          const end = row.range_end ?? row.rangeEnd ?? row.bucket_end;
                          const servable = row.servable ?? row.is_servable ?? row.servable_on_version;
                          return (
                            <TableRow key={key || index}>
                              <TableCell>{screenLabel(screens, key)}</TableCell>
                              <TableCell>{row.weight != null ? `${row.weight}%` : '—'}</TableCell>
                              <TableCell>{formatBucketRange(start, end)}</TableCell>
                              <TableCell>
                                {servable == null ? '—' : servable ? 'Yes' : 'Fallback'}
                              </TableCell>
                              <TableCell align="right">
                                {formatNumber(observed.opens ?? observed.open_count)}
                              </TableCell>
                              <TableCell align="right">
                                {formatNumber(observed.users ?? observed.user_count)}
                              </TableCell>
                              <TableCell align="right">
                                {formatPercent(observed.return_rate)}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {distRows.length === 0 && statsRows.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={7}>
                              <Typography color="text.secondary">No stats yet.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}

          {tab === 'preview' && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Fastest way to answer “why did this device land there”. Pass installation_id, user_id, or both.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <TextField
                  label="installation_id"
                  value={previewForm.installation_id}
                  onChange={(e) => setPreviewForm((p) => ({ ...p, installation_id: e.target.value }))}
                />
                <TextField
                  label="user_id"
                  value={previewForm.user_id}
                  onChange={(e) => setPreviewForm((p) => ({ ...p, user_id: e.target.value }))}
                />
                <TextField
                  label="app_version"
                  placeholder="2.4.0"
                  value={previewForm.app_version}
                  onChange={(e) => setPreviewForm((p) => ({ ...p, app_version: e.target.value }))}
                />
                <TextField
                  select
                  label="platform"
                  value={previewForm.platform}
                  onChange={(e) => setPreviewForm((p) => ({ ...p, platform: e.target.value }))}
                >
                  <MenuItem value="android">android</MenuItem>
                  <MenuItem value="ios">ios</MenuItem>
                </TextField>
              </Box>
              <Button variant="contained" onClick={handlePreview} disabled={previewing}>
                {previewing ? 'Checking…' : 'Preview assignment'}
              </Button>

              {previewResult && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Lands on {screenLabel(screens, previewScreenKey)}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {previewResult.reason && (
                      <Chip label={`reason: ${previewResult.reason}`} color="primary" />
                    )}
                    {previewResult.bucket != null && <Chip label={`bucket ${previewResult.bucket}`} />}
                    {(previewResult.rule_name || previewResult.ruleName) && (
                      <Chip
                        variant="outlined"
                        label={`rule: ${previewResult.rule_name || previewResult.ruleName}`}
                      />
                    )}
                    {(previewResult.variant_key || previewResult.variantKey) && (
                      <Chip
                        variant="outlined"
                        label={`variant: ${previewResult.variant_key || previewResult.variantKey}`}
                      />
                    )}
                    {previewResult.is_logged_in != null && (
                      <Chip
                        variant="outlined"
                        label={previewResult.is_logged_in ? 'logged in' : 'guest'}
                      />
                    )}
                    {previewResult.is_test_user ? <Chip variant="outlined" label="test user" /> : null}
                  </Box>
                  {previewResult.first_seen_at && (
                    <Typography variant="body2" color="text.secondary">
                      First seen {formatDateTime(previewResult.first_seen_at)}
                    </Typography>
                  )}
                  <Box
                    component="pre"
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      overflow: 'auto',
                      fontSize: 12,
                    }}
                  >
                    {JSON.stringify(previewResult, null, 2)}
                  </Box>
                </Box>
              )}
            </Paper>
          )}

          {tab === 'overrides' && (
            <Box>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Pin a QA / demo device
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Overrides beat every rule. An installation override wins over a user override for the same
                  device.
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr auto' }, gap: 2 }}>
                  <TextField
                    select
                    size="small"
                    label="subject_type"
                    value={overrideForm.subject_type}
                    onChange={(e) => setOverrideForm((p) => ({ ...p, subject_type: e.target.value }))}
                  >
                    {SUBJECT_TYPE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    label="subject_key"
                    placeholder="installation_id or users.id"
                    value={overrideForm.subject_key}
                    onChange={(e) => setOverrideForm((p) => ({ ...p, subject_key: e.target.value }))}
                  />
                  <TextField
                    select
                    size="small"
                    label="screen"
                    value={overrideForm.screen_key}
                    onChange={(e) => setOverrideForm((p) => ({ ...p, screen_key: e.target.value }))}
                  >
                    {screens.map((s) => (
                      <MenuItem key={s.screen_key} value={s.screen_key}>
                        {s.label || s.screen_key}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    label="note"
                    placeholder="QA Pixel 7"
                    value={overrideForm.note}
                    onChange={(e) => setOverrideForm((p) => ({ ...p, note: e.target.value }))}
                  />
                  <Button variant="contained" onClick={handleSaveOverride} disabled={savingOverride}>
                    {savingOverride ? 'Saving…' : 'Upsert'}
                  </Button>
                </Box>
              </Paper>

              {overridesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Subject</TableCell>
                        <TableCell>Screen</TableCell>
                        <TableCell>Note</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {overrides.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5}>
                            <Typography color="text.secondary">No overrides.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        overrides.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.subject_type || row.subjectType}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                              {row.subject_key || row.subjectKey}
                            </TableCell>
                            <TableCell>
                              {screenLabel(screens, row.screen_key || row.screenKey)}
                            </TableCell>
                            <TableCell>{row.note || '—'}</TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                color="error"
                                disabled={deletingOverrideId === row.id}
                                onClick={() => handleDeleteOverride(row)}
                              >
                                {deletingOverrideId === row.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <DeleteIcon fontSize="small" />
                                )}
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {tab === 'screens' && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Key</TableCell>
                    <TableCell>Label</TableCell>
                    <TableCell>Route</TableCell>
                    <TableCell>Deep link</TableCell>
                    <TableCell>Min version</TableCell>
                    <TableCell>Sort</TableCell>
                    <TableCell>Enabled</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {screens.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography color="text.secondary">No screens.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    [...screens]
                      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
                      .map((row) => (
                        <TableRow key={row.screen_key}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{row.screen_key}</TableCell>
                          <TableCell>{row.label}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {row.navigation_link || '—'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {row.deep_link || '—'}
                          </TableCell>
                          <TableCell>{row.min_app_version || '—'}</TableCell>
                          <TableCell>{row.sort_order ?? 0}</TableCell>
                          <TableCell>
                            <Switch
                              size="small"
                              checked={!!row.is_enabled}
                              disabled={togglingScreenKey === row.screen_key}
                              onChange={() => handleToggleScreen(row)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => openEditScreen(row)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 'audit' && (
            <Box>
              {auditLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>When</TableCell>
                        <TableCell>Who</TableCell>
                        <TableCell>Action</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Typography color="text.secondary">No audit entries.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        auditRows.map((row, index) => {
                          const id = row.id ?? index;
                          const expanded = expandedAudit === id;
                          return (
                            <React.Fragment key={id}>
                              <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpandedAudit(expanded ? null : id)}>
                                <TableCell>
                                  {formatDateTime(row.created_at || row.createdAt || row.timestamp)}
                                </TableCell>
                                <TableCell>
                                  {row.actor || row.admin_email || row.user || row.created_by || '—'}
                                </TableCell>
                                <TableCell>
                                  {row.action || row.event || row.operation || '—'}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="caption" color="text.secondary">
                                    {expanded ? 'hide' : 'before / after'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                              {expanded && (
                                <TableRow>
                                  <TableCell colSpan={4}>
                                    <Box
                                      component="pre"
                                      sx={{
                                        p: 1.5,
                                        bgcolor: 'grey.100',
                                        borderRadius: 1,
                                        overflow: 'auto',
                                        fontSize: 12,
                                        m: 0,
                                      }}
                                    >
                                      {JSON.stringify(
                                        {
                                          before: row.before || row.before_json || row.previous,
                                          after: row.after || row.after_json || row.next,
                                        },
                                        null,
                                        2
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </div>
      </div>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => !savingRule && setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, height: '100%', overflow: 'auto' }}>
          <Typography variant="h6">{editingRule ? `Edit ${editingRule.name}` : 'New rule'}</Typography>
          {ruleFormError && (
            <Alert severity="error" onClose={() => setRuleFormError('')}>
              {ruleFormError}
            </Alert>
          )}

          <TextField
            label="Name"
            required
            fullWidth
            value={ruleForm.name}
            onChange={(e) => setRuleField('name', e.target.value)}
            error={!!ruleErrors.name}
            helperText={ruleErrors.name || '≤ 120 characters'}
            disabled={savingRule}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Priority"
              type="number"
              value={ruleForm.priority}
              onChange={(e) => setRuleField('priority', e.target.value)}
              error={!!ruleErrors.priority}
              helperText={ruleErrors.priority || 'Lower runs first'}
              disabled={savingRule}
            />
            <TextField
              select
              label="Audience"
              value={ruleForm.audience}
              onChange={(e) => setRuleField('audience', e.target.value)}
              disabled={savingRule}
            >
              {AUDIENCE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Platform"
              value={ruleForm.platform}
              onChange={(e) => setRuleField('platform', e.target.value)}
              disabled={savingRule}
            >
              {PLATFORM_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Fallback screen"
              value={ruleForm.fallback_screen_key}
              onChange={(e) => setRuleField('fallback_screen_key', e.target.value)}
              helperText="Used when the build cannot open the assigned tab"
              disabled={savingRule}
            >
              {screens.map((s) => (
                <MenuItem key={s.screen_key} value={s.screen_key}>
                  {s.label || s.screen_key}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Min app version"
              placeholder="2.4.0"
              value={ruleForm.min_app_version}
              onChange={(e) => setRuleField('min_app_version', e.target.value)}
              disabled={savingRule}
            />
            <TextField
              label="Max app version (exclusive)"
              placeholder="3.0.0"
              value={ruleForm.max_app_version}
              onChange={(e) => setRuleField('max_app_version', e.target.value)}
              disabled={savingRule}
            />
            <TextField
              label="Starts at"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={ruleForm.starts_at}
              onChange={(e) => setRuleField('starts_at', e.target.value)}
              disabled={savingRule}
            />
            <TextField
              label="Ends at"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={ruleForm.ends_at}
              onChange={(e) => setRuleField('ends_at', e.target.value)}
              error={!!ruleErrors.ends_at}
              helperText={ruleErrors.ends_at}
              disabled={savingRule}
            />
          </Box>

          {(ruleForm.audience === 'new_users' || ruleForm.audience === 'existing_users') && (
            <TextField
              label="New user since"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={ruleForm.new_user_since}
              onChange={(e) => setRuleField('new_user_since', e.target.value)}
              helperText="Cut-off for first seen. Defaults to rule created_at if empty."
              disabled={savingRule}
            />
          )}

          <TextField
            label="Salt"
            value={ruleForm.salt}
            onChange={(e) => setRuleField('salt', e.target.value)}
            helperText="Change this to re-randomise every assignment for this rule"
            disabled={savingRule}
            InputProps={{
              endAdornment: (
                <Button size="small" onClick={() => setRuleField('salt', randomSalt())} disabled={savingRule}>
                  New
                </Button>
              ),
            }}
          />

          <TextField
            label="Notes"
            multiline
            minRows={2}
            value={ruleForm.notes}
            onChange={(e) => setRuleField('notes', e.target.value)}
            disabled={savingRule}
          />

          <FormControlLabel
            control={
              <Switch
                checked={!!ruleForm.is_enabled}
                onChange={(e) => setRuleField('is_enabled', e.target.checked)}
                disabled={savingRule}
              />
            }
            label={ruleForm.is_enabled ? 'Enabled' : 'Disabled'}
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!ruleForm.exclude_test_users}
                onChange={(e) => setRuleField('exclude_test_users', e.target.checked)}
                disabled={savingRule}
              />
            }
            label="Exclude admin-created test users"
          />

          <Typography variant="subtitle2">Weights</Typography>
          <WeightEditor
            variants={ruleForm.variants}
            screens={screens}
            disabled={savingRule}
            onChange={(variants) => setRuleField('variants', variants)}
          />

          <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 2 }}>
            <Button onClick={() => setDrawerOpen(false)} disabled={savingRule}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveRule}
              disabled={savingRule}
              startIcon={savingRule ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {savingRule ? 'Saving…' : editingRule ? 'Save rule' : 'Create rule'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Dialog open={screenDialogOpen} onClose={() => !savingScreen && setScreenDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingScreen ? `Edit ${editingScreen.screen_key}` : 'Add screen'}</DialogTitle>
        <DialogContent>
          {screenFormError && (
            <Alert severity="error" sx={{ mt: 1, mb: 1 }} onClose={() => setScreenFormError('')}>
              {screenFormError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="screen_key"
              value={screenForm.screen_key}
              onChange={(e) => setScreenForm((p) => ({ ...p, screen_key: e.target.value.toLowerCase() }))}
              disabled={!!editingScreen || savingScreen}
              error={!!screenErrors.screen_key}
              helperText={screenErrors.screen_key || 'lowercase [a-z][a-z0-9_]{0,63}'}
            />
            <TextField
              label="Label"
              value={screenForm.label}
              onChange={(e) => setScreenForm((p) => ({ ...p, label: e.target.value }))}
              disabled={savingScreen}
              error={!!screenErrors.label}
              helperText={screenErrors.label}
            />
            <TextField
              label="Navigation link"
              placeholder="/arcade"
              value={screenForm.navigation_link}
              onChange={(e) => setScreenForm((p) => ({ ...p, navigation_link: e.target.value }))}
              disabled={savingScreen}
            />
            <TextField
              label="Deep link"
              placeholder="dugout://arcade"
              value={screenForm.deep_link}
              onChange={(e) => setScreenForm((p) => ({ ...p, deep_link: e.target.value }))}
              disabled={savingScreen}
            />
            <TextField
              label="Min app version"
              placeholder="2.5.0"
              value={screenForm.min_app_version}
              onChange={(e) => setScreenForm((p) => ({ ...p, min_app_version: e.target.value }))}
              helperText="Floor per screen so old builds never open a tab they cannot render"
              disabled={savingScreen}
            />
            <TextField
              label="Sort order"
              type="number"
              value={screenForm.sort_order}
              onChange={(e) => setScreenForm((p) => ({ ...p, sort_order: e.target.value }))}
              error={!!screenErrors.sort_order}
              helperText={screenErrors.sort_order}
              disabled={savingScreen}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!!screenForm.is_enabled}
                  onChange={(e) => setScreenForm((p) => ({ ...p, is_enabled: e.target.checked }))}
                  disabled={savingScreen}
                />
              }
              label={screenForm.is_enabled ? 'Enabled' : 'Disabled'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScreenDialogOpen(false)} disabled={savingScreen}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveScreen} disabled={savingScreen}>
            {savingScreen ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteRule} onClose={() => !deletingRule && setDeleteRule(null)}>
        <DialogTitle>Delete rule?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete “{deleteRule?.name}”? The API refuses to delete the last rule — disable it instead.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRule(null)} disabled={deletingRule}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDeleteRule} disabled={deletingRule}>
            {deletingRule ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!reshuffleRule} onClose={() => !reshuffling && setReshuffleRule(null)}>
        <DialogTitle>Re-randomise assignments?</DialogTitle>
        <DialogContent>
          <Typography>
            Changing the salt on “{reshuffleRule?.name}” re-rolls every device assigned by this rule. Most
            weight edits only move people who cross a boundary — this one moves everybody.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReshuffleRule(null)} disabled={reshuffling}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleReshuffle} disabled={reshuffling} startIcon={<ShuffleIcon />}>
            {reshuffling ? 'Updating…' : 'Change salt'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Component>
  );
}

export default AppLanding;
